import { create } from 'zustand';
import { CompanyAccountingSummary, VehicleCost } from '../types';
import { supabase } from '../api/supabase';

// ===== TYPE DEFINITIONS =====

export interface CompanyAccountingFilters {
  year?: number;
  month?: number;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
}

export interface ProfitTrend {
  month: number;
  year: number;
  period: string; // "2025-10"
  gross_profit: number;
  net_profit: number;
  vehicle_count: number;
  driver_count: number;
  profit_per_vehicle: number;
}

export interface VehicleProfitability {
  make_model: string;
  year: number;
  vehicle_count: number;
  total_cost: number;
  total_rent_income: number;
  profit: number;
  profit_per_vehicle: number;
}

// ===== STORE INTERFACE =====

interface CompanyAccountingStore {
  summaries: CompanyAccountingSummary[];
  selectedSummary: CompanyAccountingSummary | null;
  vehicleCosts: VehicleCost[];
  profitTrends: ProfitTrend[];
  vehicleProfitability: VehicleProfitability[];
  loading: boolean;
  error: string | null;

  // CRUD operations
  fetchSummaries: (filters?: CompanyAccountingFilters) => Promise<void>;
  fetchSummaryById: (id: string) => Promise<void>;
  fetchSummaryByPeriod: (year: number, month: number) => Promise<CompanyAccountingSummary | null>;
  calculateCompanyProfit: (year: number, month: number) => Promise<string>;
  refreshCurrentMonthProfit: () => Promise<string>;
  
  // Vehicle costs management
  fetchVehicleCosts: () => Promise<void>;
  updateVehicleCost: (id: string, updates: Partial<VehicleCost>) => Promise<void>;
  addVehicleCost: (costData: Omit<VehicleCost, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  deleteVehicleCost: (id: string) => Promise<void>;
  
  // Analytics & Reports
  fetchProfitTrends: (months: number) => Promise<void>;
  fetchVehicleProfitability: (year: number, month: number) => Promise<void>;
  
  // Helper functions
  setSelectedSummary: (summary: CompanyAccountingSummary | null) => void;
  clearError: () => void;
}

// ===== STORE IMPLEMENTATION =====

export const useCompanyAccountingStore = create<CompanyAccountingStore>((set, get) => ({
  summaries: [],
  selectedSummary: null,
  vehicleCosts: [],
  profitTrends: [],
  vehicleProfitability: [],
  loading: false,
  error: null,

  fetchSummaries: async (filters?: CompanyAccountingFilters) => {
    set({ loading: true, error: null });
    try {
      let query = supabase
        .from('company_accounting_summary')
        .select('*')
        .order('period_year', { ascending: false })
        .order('period_month', { ascending: false });

      // Apply filters
      if (filters?.year) {
        query = query.eq('period_year', filters.year);
      }
      if (filters?.month) {
        query = query.eq('period_month', filters.month);
      }
      if (filters?.startYear && filters?.startMonth) {
        query = query.gte('period_year', filters.startYear);
        query = query.gte('period_month', filters.startMonth);
      }
      if (filters?.endYear && filters?.endMonth) {
        query = query.lte('period_year', filters.endYear);
        query = query.lte('period_month', filters.endMonth);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ summaries: data || [], loading: false });
    } catch (error: any) {
      console.error('Error fetching company accounting summaries:', error);
      set({ error: error.message, loading: false });
    }
  },

  fetchSummaryById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('company_accounting_summary')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      set({ selectedSummary: data, loading: false });
    } catch (error: any) {
      console.error('Error fetching summary:', error);
      set({ error: error.message, loading: false });
    }
  },

  fetchSummaryByPeriod: async (year: number, month: number) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('company_accounting_summary')
        .select('*')
        .eq('period_year', year)
        .eq('period_month', month)
        .single();

      if (error) {
        // Summary doesn't exist yet, return null
        if (error.code === 'PGRST116') {
          set({ selectedSummary: null, loading: false });
          return null;
        }
        throw error;
      }

      set({ selectedSummary: data, loading: false });
      return data;
    } catch (error: any) {
      console.error('Error fetching summary by period:', error);
      set({ error: error.message, loading: false });
      return null;
    }
  },

  calculateCompanyProfit: async (year: number, month: number) => {
    set({ loading: true, error: null });
    try {
      // Call the PostgreSQL function to calculate and store profit
      const { data: summaryId, error } = await supabase.rpc('calculate_company_profit', {
        p_period_month: month,
        p_period_year: year,
      });

      if (error) throw error;

      set({ loading: false });

      // Refresh summaries to include the new one
      await get().fetchSummaries({ year, month });

      // Set as selected summary
      await get().fetchSummaryById(summaryId);

      return summaryId;
    } catch (error: any) {
      console.error('Error calculating company profit:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  refreshCurrentMonthProfit: async () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

    return get().calculateCompanyProfit(currentYear, currentMonth);
  },

  fetchVehicleCosts: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('vehicle_costs')
        .select('*')
        .order('make_model', { ascending: true })
        .order('year', { ascending: false });

      if (error) throw error;

      set({ vehicleCosts: data || [], loading: false });
    } catch (error: any) {
      console.error('Error fetching vehicle costs:', error);
      set({ error: error.message, loading: false });
    }
  },

  updateVehicleCost: async (id: string, updates: Partial<VehicleCost>) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('vehicle_costs')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        vehicleCosts: state.vehicleCosts.map((vc) =>
          vc.id === id ? { ...vc, ...updates, updated_at: new Date().toISOString() } : vc
        ),
        loading: false,
      }));

      // Refresh profit calculations if cost changed
      await get().refreshCurrentMonthProfit();
    } catch (error: any) {
      console.error('Error updating vehicle cost:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  addVehicleCost: async (costData: Omit<VehicleCost, 'id' | 'created_at' | 'updated_at'>) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('vehicle_costs')
        .insert([costData])
        .select()
        .single();

      if (error) throw error;

      // Update local state
      set((state) => ({
        vehicleCosts: [...state.vehicleCosts, data],
        loading: false,
      }));

      // Refresh profit calculations
      await get().refreshCurrentMonthProfit();
    } catch (error: any) {
      console.error('Error adding vehicle cost:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteVehicleCost: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.from('vehicle_costs').delete().eq('id', id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        vehicleCosts: state.vehicleCosts.filter((vc) => vc.id !== id),
        loading: false,
      }));

      // Refresh profit calculations
      await get().refreshCurrentMonthProfit();
    } catch (error: any) {
      console.error('Error deleting vehicle cost:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchProfitTrends: async (months: number = 6) => {
    set({ loading: true, error: null });
    try {
      const now = new Date();
      const endDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const startDate = new Date(endDate);
      startDate.setMonth(startDate.getMonth() - months + 1);

      const { data, error } = await supabase
        .from('company_accounting_summary')
        .select('*')
        .gte('period_year', startDate.getFullYear())
        .lte('period_year', endDate.getFullYear())
        .order('period_year', { ascending: true })
        .order('period_month', { ascending: true });

      if (error) throw error;

      // Transform to ProfitTrend format
      const trends: ProfitTrend[] = (data || []).map((summary) => ({
        month: summary.period_month,
        year: summary.period_year,
        period: `${summary.period_year}-${String(summary.period_month).padStart(2, '0')}`,
        gross_profit: summary.gross_profit,
        net_profit: summary.net_profit,
        vehicle_count: summary.vehicle_count,
        driver_count: summary.active_driver_count,
        profit_per_vehicle: summary.profit_per_vehicle,
      }));

      set({ profitTrends: trends, loading: false });
    } catch (error: any) {
      console.error('Error fetching profit trends:', error);
      set({ error: error.message, loading: false });
    }
  },

  fetchVehicleProfitability: async (year: number, month: number) => {
    set({ loading: true, error: null });
    try {
      // Fetch vehicle costs grouped by make/model/year
      const { data: costs, error: costsError } = await supabase
        .from('vehicle_costs')
        .select('make_model, year, cost_per_month');

      if (costsError) throw costsError;

      // Fetch settlements for the period to get rent income per vehicle
      const { data: settlements, error: settlementsError } = await supabase
        .from('settlement_records')
        .select(`
          total_rent_charged,
          vehicle:vehicles!vehicle_id(make_model, year)
        `)
        .eq('period_year', year)
        .eq('period_month', month)
        .neq('status', 'cancelled');

      if (settlementsError) throw settlementsError;

      // Group by make_model + year
      const profitabilityMap = new Map<string, VehicleProfitability>();

      // Add costs
      (costs || []).forEach((cost) => {
        const key = `${cost.make_model}_${cost.year}`;
        profitabilityMap.set(key, {
          make_model: cost.make_model,
          year: cost.year,
          vehicle_count: 0,
          total_cost: cost.cost_per_month,
          total_rent_income: 0,
          profit: 0,
          profit_per_vehicle: 0,
        });
      });

      // Add rent income
      (settlements || []).forEach((settlement: any) => {
        const vehicle = Array.isArray(settlement.vehicle) 
          ? settlement.vehicle[0] 
          : settlement.vehicle;
        
        if (vehicle) {
          const key = `${vehicle.make_model}_${vehicle.year}`;
          const existing = profitabilityMap.get(key);
          
          if (existing) {
            existing.vehicle_count += 1;
            existing.total_rent_income += settlement.total_rent_charged;
          } else {
            // No cost entry, create one
            profitabilityMap.set(key, {
              make_model: vehicle.make_model,
              year: vehicle.year,
              vehicle_count: 1,
              total_cost: 0,
              total_rent_income: settlement.total_rent_charged,
              profit: 0,
              profit_per_vehicle: 0,
            });
          }
        }
      });

      // Calculate profits
      const profitability: VehicleProfitability[] = Array.from(profitabilityMap.values()).map((item) => {
        const profit = item.total_rent_income - item.total_cost;
        return {
          ...item,
          profit,
          profit_per_vehicle: item.vehicle_count > 0 ? profit / item.vehicle_count : 0,
        };
      });

      set({ vehicleProfitability: profitability, loading: false });
    } catch (error: any) {
      console.error('Error fetching vehicle profitability:', error);
      set({ error: error.message, loading: false });
    }
  },

  setSelectedSummary: (summary: CompanyAccountingSummary | null) => {
    set({ selectedSummary: summary });
  },

  clearError: () => {
    set({ error: null });
  },
}));
